import { Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Product } from "@domain/product/product";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { Regard3000Service } from "@project/gas-warning-center-configuration/regard-3000/regard-3000.service";
import { ProductItem } from "@project/product-item";
import { Subscription } from "rxjs";

@Component({
  selector: "app-lid-unit-selector",
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    FilterProductComponent,
    ProductCardWithQuantityComponent,
    RefreshProductsHintComponent,
  ],
  templateUrl: "./lid-unit-selector.component.html",
  styleUrl: "./lid-unit-selector.component.scss",
  standalone: true,
})
export class LidUnitSelectorComponent {
  private regard3000Service = inject(Regard3000Service);
  private productService: ProductService = inject(ProductService);
  readonly type = input<GasWarningCenterType>();
  readonly originalLidUnits = input<ProductItem[]>();
  readonly selectedBaseUnits = input.required<ProductWithQuantity[]>();
  readonly showCosts = input.required<boolean>();
  readonly selectionMode: "single" | "multi" = "multi";
  readonly isLidSelectionValid = output<boolean>();
  readonly selectedLidUnitsChange = output<ProductWithQuantity[]>();
  readonly searchQuery = signal<string>("");

  readonly filteredLidUnits = computed(() => {
    const list = this.lidUnits();
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return list;

    return list.filter((lidUnit) => {
      const name = lidUnit.name?.toLowerCase() ?? "";
      const id = String(lidUnit.id ?? "").toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  });

  readonly lidUnitSelectionValid = computed(() => {
    const requiredCount = this.requiredLidUnitQuantity();
    const currentCount = this.regard3000Service.sumOfQuantity(this.selectedLidUnits());
    return currentCount === requiredCount;
  });
  readonly requiredLidUnitQuantity = computed(() => {
    const selectedBaseUnits = this.selectedBaseUnits();
    return this.regard3000Service.sumOfQuantity(selectedBaseUnits);
  });

  protected readonly lidUnits = signal<Product[]>([]);
  protected readonly selectedLidUnits = signal<ProductWithQuantity[]>([]);
  protected readonly fallbackImageUrl = computed(() => {
    const cantoImageId = this.type()?.cantoImageId;
    return cantoImageId ? ProductImage.getCantoSourceUrl(cantoImageId) : undefined;
  });
  constructor() {
    effect((onCleanup) => {
      const originalLidUnits = this.originalLidUnits();
      if (!originalLidUnits) {
        this.selectedLidUnits.set([]);
        this.selectedLidUnitsChange.emit([]);
        return;
      }

      const subscriptions: Subscription[] = [];
      for (const lidUnit of originalLidUnits) {
        subscriptions.push(
          this.productService.getProductById(lidUnit.productId).subscribe((product) => {
            if (product) {
              this.updateSelectedLidUnits(lidUnit.quantity, product);
            }
          }),
        );
      }
      onCleanup(() => subscriptions.forEach((subscription) => subscription.unsubscribe()));
    });
    effect((onCleanup) => {
      const type = this.type();
      const selectedBaseUnits = this.selectedBaseUnits();
      if (!selectedBaseUnits.length) {
        this.selectedLidUnits.set([]);
        this.selectedLidUnitsChange.emit([]);
      }

      if (!type || !selectedBaseUnits.length) {
        this.lidUnits.set([]);
        return;
      }

      const subscription = this.regard3000Service
        .getLidUnitProducts(selectedBaseUnits.map(({ product }) => product))
        .subscribe((products) => {
          this.lidUnits.set(products);
        });
      onCleanup(() => subscription.unsubscribe());
    });
    effect(() => {
      this.selectedLidUnits();
    });
    effect(() => {
      const isValid = this.lidUnitSelectionValid();
      this.isLidSelectionValid.emit(isValid);
    });

    effect(() => {
      const selectedLidUnits = this.selectedLidUnits();
      const lidUnits = this.lidUnits();
      const requiredQuantity = this.requiredLidUnitQuantity();
      if (!lidUnits.length || !requiredQuantity) return;
      let quantity = 0;

      const value: ProductWithQuantity[] = lidUnits.map((lidUnit) => {
        quantity = 0;

        const selectedLidUnit = selectedLidUnits.find((selectedLidUnit) => selectedLidUnit.product.id === lidUnit.id);
        if (selectedLidUnit) {
          quantity = selectedLidUnit.quantity;
        }
        return {
          product: lidUnit,
          quantity: quantity,
        };
      });
      this.selectedLidUnitsChange.emit(value.filter((item) => item.quantity > 0));
    });
  }

  protected updateSelectedLidUnits(quantity: number, selectedLidUnit: Product): void {
    if (!selectedLidUnit?.id) return;

    this.selectedLidUnits.update((list) => {
      const index = list.findIndex((lidUnit) => lidUnit.product.id === selectedLidUnit.id);

      if (quantity <= 0) {
        if (index === -1) return list;
        return list.filter((_, i) => i !== index);
      }

      if (index === -1) {
        return [...list, { product: selectedLidUnit, quantity: Math.floor(quantity) }];
      }

      const updated = [...list];
      updated[index] = { ...updated[index], quantity: Math.floor(quantity) };
      return updated;
    });
  }

  protected getSelectedLidUnitQuantity(lidUnit: Product) {
    const result = this.selectedLidUnits().find((selectedLidUnit) => selectedLidUnit.product?.id === lidUnit.id);
    return result?.quantity ?? 0;
  }

  protected getLidUnitImageUrl(lidUnit: Product): string | undefined {
    return lidUnit.cantoImageId ? ProductImage.getCantoSourceUrl(lidUnit.cantoImageId) : this.fallbackImageUrl();
  }

  protected onLidUnitSelectionChange(quantity: number, lidUnit: Product) {
    if (!lidUnit) return;
    this.updateSelectedLidUnits(quantity, lidUnit);
  }

  protected search(query: string) {
    this.searchQuery.set(query ?? "");
  }
}
